"use client";

import { Icon } from "./Icon";
import { cn } from "@/lib/utils";
import { agentGlow } from "@/lib/agentsUI";

interface AgentRoleIconProps {
  slug: string;
  /** Nom d'icône Lucide (voir AGENT_UI dans lib/agents.ts, ex. "LifeBuoy" pour Mimo). */
  icon: string;
  /** Libellé du métier, écrit sur l'icône (ex. "Service client"). */
  role: string;
  size?: number;
  className?: string;
  /** Force l'affichage/masquage du libellé. Par défaut : masqué en dessous de
   *  90px, le texte devenant illisible aux tailles des chips de navigation. */
  showLabel?: boolean;
}

/**
 * Avatar alternatif "icône de métier" (test A/B face aux figurines Funko) :
 * un pictogramme Lucide représentant le rôle de l'agent, avec le nom du
 * métier écrit directement dessus. Voir AVATAR_MAP / AgentAvatar pour le
 * style actuel (photos) -- celui-ci n'est branché que sur un seul agent pour
 * validation avant généralisation.
 */
export function AgentRoleIcon({ slug, icon, role, size = 240, className, showLabel }: AgentRoleIconProps) {
  const glow = agentGlow(slug);
  const label = showLabel ?? size >= 90;
  return (
    <div
      className={cn("relative inline-flex items-center justify-center shrink-0", className)}
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.22,
        background: `radial-gradient(circle at 50% 32%, ${glow}, transparent 72%), var(--color-bg-soft)`,
        border: "1px solid var(--color-line)",
        boxShadow: "0 20px 50px -24px rgba(0,0,0,0.35)",
      }}
    >
      <Icon
        name={icon}
        size={label ? size * 0.4 : size * 0.52}
        strokeWidth={1.4}
        style={{ color: "var(--color-ink)" }}
      />
      {label && (
        <span
          className="absolute left-1/2 -translate-x-1/2 rounded-full px-3.5 py-1.5 text-[12px] font-black uppercase tracking-wide whitespace-nowrap"
          style={{
            bottom: size * 0.1,
            background: "var(--color-ink)",
            color: "#fff",
          }}
        >
          {role}
        </span>
      )}
    </div>
  );
}
